import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Pricing from "@/pages/Pricing";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PaymentResult from "@/pages/PaymentResult";
import Auth from "@/pages/Auth";
import Refunds from "@/pages/Refunds";

import AiAssistant from "@/components/site/AiAssistant";

import ProtectedRoute from "@/components/site/ProtectedRoute";
import DashboardLayout from "@/components/site/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Returns from "@/pages/dashboard/Returns";
import ReturnPrint from "@/pages/dashboard/ReturnPrint";
import BasicInfo from "@/pages/dashboard/BasicInfo";
import Invoices, { InvoicePrint } from "@/pages/dashboard/Invoices";
import Notices from "@/pages/dashboard/Notices";
import Payments from "@/pages/dashboard/Payments";
import Documents from "@/pages/dashboard/Documents";
import Workflow from "@/pages/dashboard/Workflow";
import Analytics from "@/pages/dashboard/Analytics";
import Users from "@/pages/dashboard/Users";
import BlogAdmin from "@/pages/dashboard/BlogAdmin";
import Security from "@/pages/dashboard/Security";
import Employees from "@/pages/dashboard/Employees";
import AuditLog from "@/pages/dashboard/AuditLog";

const ADMIN_ONLY = ["admin"];
const FIRM_ONLY = ["admin", "cpa"];
const STAFF_ONLY = ["admin", "cpa", "caller", "document_team"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Navigate to="/auth?tab=login" replace />} />
            <Route path="/register" element={<Navigate to="/auth?tab=register" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/payment/success" element={<PaymentResult success={true} />} />
            <Route path="/payment/cancel" element={<PaymentResult success={false} />} />
            <Route path="/portal" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="basic-info" element={<BasicInfo />} />
              <Route path="returns" element={<Returns />} />
              <Route path="returns/:returnId/print" element={<ReturnPrint />} />
              <Route path="notices" element={<Notices />} />
              <Route
                path="payments"
                element={
                  <ProtectedRoute roles={STAFF_ONLY}>
                    <Payments />
                  </ProtectedRoute>
                }
              />
              <Route path="documents" element={<Documents />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/:invoiceId/print" element={<InvoicePrint />} />
              <Route
                path="workflow"
                element={
                  <ProtectedRoute roles={FIRM_ONLY}>
                    <Workflow />
                  </ProtectedRoute>
                }
              />
              <Route path="analytics" element={<Analytics />} />
              <Route path="security" element={<Security />} />
              <Route
                path="blog-cms"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <BlogAdmin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Users />
                  </ProtectedRoute>
                }
              />
              <Route
                path="employees"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Employees />
                  </ProtectedRoute>
                }
              />
              <Route
                path="audit-log"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <AuditLog />
                  </ProtectedRoute>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
          <AiAssistant />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
